import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DownloadSimple, FileText, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { normalizeLang } from "@/lib/i18n";
import { getInterviewReturnTarget } from "@/lib/activeInterviewNavigation";
import { toast } from "sonner";

export const COPY = {
  en: {
    menu: "My Reports & Downloads",
    heading: "Your Interview Reports",
    intro: "Your Interview Reports can be downloaded until your 30-day access ends. Then they are permanently deleted, with the résumé, job description and answers they were built from.",
    back: "Back to interview practice",
    empty: "You don't have any reports available to download.",
    quick: "Quick Practice",
    advanced: "Advanced Practice",
    repractice: "Re-practice",
    completed: "Completed",
    expires: "Available until",
    report: "Download Performance Report",
    error: "We couldn't load your downloads. Please try again.",
  },
  "pt-BR": {
    menu: "Meus Relatórios e Downloads",
    heading: "Seus Relatórios de Entrevista",
    intro: "Seus Relatórios de Entrevista podem ser baixados até o fim do seu acesso de 30 dias. Depois disso, eles são excluídos permanentemente, junto com o currículo, a descrição da vaga e as respostas usados para criá-los.",
    back: "Voltar à prática de entrevista",
    empty: "Você não tem relatórios disponíveis para download.",
    quick: "Prática rápida",
    advanced: "Prática avançada",
    repractice: "Nova prática",
    completed: "Concluída",
    expires: "Disponível até",
    report: "Baixar Relatório de Desempenho",
    error: "Não foi possível carregar seus downloads. Tente novamente.",
  },
  "es-419": {
    menu: "Mis Informes y Descargas",
    heading: "Sus Informes de Entrevista",
    intro: "Sus Informes de Entrevista se pueden descargar hasta que termine su acceso de 30 días. Después se eliminan permanentemente, junto con el currículum, la descripción del puesto y las respuestas con que se crearon.",
    back: "Volver a la práctica de entrevista",
    empty: "No tiene informes disponibles para descargar.",
    quick: "Práctica rápida",
    advanced: "Práctica avanzada",
    repractice: "Nueva práctica",
    completed: "Completada",
    expires: "Disponible hasta",
    report: "Descargar Informe de Desempeño",
    error: "No pudimos cargar sus descargas. Inténtelo de nuevo.",
  },
  "fr-CA": {
    menu: "Mes rapports et téléchargements",
    heading: "Vos rapports d’entrevue",
    intro: "Vos rapports d’entrevue peuvent être téléchargés jusqu’à la fin de votre accès de 30 jours. Ils sont ensuite supprimés définitivement, avec le CV, la description de poste et les réponses qui ont servi à les créer.",
    back: "Retour à la pratique d’entrevue",
    empty: "Vous n’avez aucun rapport disponible au téléchargement.",
    quick: "Pratique rapide",
    advanced: "Pratique avancée",
    repractice: "Nouvelle pratique",
    completed: "Terminée",
    expires: "Disponible jusqu’au",
    report: "Télécharger le rapport de rendement",
    error: "Impossible de charger vos téléchargements. Veuillez réessayer.",
  },
};

function fmtDate(value, lang) {
  if (!value) return "—";
  try {
    const locale = lang === "pt-BR" ? "pt-BR" : lang === "es-419" ? "es" : lang === "fr-CA" ? "fr-CA" : "en-CA";
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "short", day: "numeric" }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export default function Downloads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = normalizeLang(user?.preferred_language);
  const t = COPY[lang] || COPY.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const baseURL = useMemo(() => api.defaults.baseURL || "/api", []);

  useEffect(() => {
    let alive = true;
    api.get("/my-downloads")
      .then(({ data }) => {
        if (alive) setItems(data?.items || []);
      })
      .catch(() => {
        if (alive) toast.error(t.error);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [t.error]);

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="downloads-page">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <button type="button" onClick={() => navigate(getInterviewReturnTarget("/"))} className="inline-flex items-center gap-2 text-sm text-[color:var(--brand)] hover:underline">
          <ArrowLeft size={16} /> {t.back}
        </button>

        <h1 className="mt-7 text-3xl sm:text-4xl font-semibold tracking-tight">{t.heading}</h1>
        <p className="mt-4 max-w-3xl text-[color:var(--text-secondary)] leading-relaxed">{t.intro}</p>

        {loading ? (
          <div className="mt-10 text-sm text-[color:var(--text-secondary)]">…</div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 text-sm text-[color:var(--text-secondary)]">
            {t.empty}
          </div>
        ) : (
          <div className="mt-10 space-y-4">
            {items.map((item) => {
              const format = item.is_repractice
                ? t.repractice
                : item.practice_format === "advanced" ? t.advanced : t.quick;
              const reportHref = `${baseURL}/sessions/${item.session_id}/report.pdf`;
              return (
                <section key={item.session_id} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 sm:p-6" data-testid={`download-item-${item.session_id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-[color:var(--brand)]/10 px-3 py-1 text-xs font-medium text-[color:var(--brand)]">{format}</span>
                        <span className="text-xs text-[color:var(--text-secondary)]">{t.completed}: {fmtDate(item.completed_at, lang)}</span>
                      </div>
                      <div className="mt-3 text-sm text-[color:var(--text-secondary)]">
                        {t.expires}: <span className="font-medium text-[color:var(--text-primary)]">{fmtDate(item.expires_at, lang)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
                      <a href={reportHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium">
                        <FileText size={17} /> <DownloadSimple size={16} /> {t.report}
                      </a>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
